import React, { useMemo, useState, useRef, useEffect } from 'react';
import { getBuffPrice, getRowPnl, getTradeBoot } from './helpers';
import { TradePartnerLine } from './TradePartner';
import { findSkinImage } from './skinIndex';


function getItemTypes(row) {
  if (!row?.itemType) return [];
  return Array.isArray(row.itemType) ? row.itemType : [row.itemType];
}

// ── Inline type picker ────────────────────────────────────
function TypePicker({ row, settings, onUpdateRow }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const available  = settings?.itemTypes || [];
  const current    = getItemTypes(row);
  const currentVals = new Set(current.map(t => t.value || t));

  const toggle = (type) => {
    const isOn = currentVals.has(type.value);
    const next = isOn
      ? current.filter(t => (t.value || t) !== type.value)
      : [...current, { value: type.value, label: type.label, color: type.color }];
    onUpdateRow(row, { itemType: next });
  };

  const primaryType = available.find(a => currentVals.has(a.value));

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        title="Set item type"
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '3px 9px', borderRadius: 7, cursor: 'pointer',
          border: primaryType
            ? `1px solid ${primaryType.color}55`
            : '1px solid rgba(255,255,255,0.12)',
          background: primaryType
            ? `${primaryType.color}18`
            : 'rgba(255,255,255,0.04)',
          color: primaryType?.color || 'rgba(255,255,255,0.35)',
          fontSize: '0.91rem', fontWeight: 600,
        }}
      >
        {primaryType ? primaryType.label : '+ Type'}
        {current.length > 1 && (
          <span style={{ fontSize: '0.87rem', opacity: 0.75 }}>+{current.length - 1}</span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '110%', left: 0, zIndex: 9999,
          background: '#13172a', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 10, padding: '6px 0', minWidth: 140,
          boxShadow: '0 8px 28px rgba(0,0,0,0.55)',
        }}>
          {available.map(type => {
            const active = currentVals.has(type.value);
            return (
              <button
                key={type.value}
                onMouseDown={e => { e.stopPropagation(); toggle(type); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', textAlign: 'left',
                  padding: '7px 14px', border: 'none', cursor: 'pointer',
                  background: active ? 'rgba(255,255,255,0.06)' : 'transparent',
                  color: active ? type.color : 'var(--text-muted)',
                  fontSize: '0.95rem', fontWeight: active ? 700 : 400,
                }}
              >
                <span style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  background: active ? type.color : 'rgba(255,255,255,0.18)',
                }} />
                {type.label}
                {active && <span style={{ marginLeft: 'auto', fontSize: '0.91rem' }}>✓</span>}
              </button>
            );
          })}
          {current.length > 0 && (
            <>
              <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '4px 0' }} />
              <button
                onMouseDown={e => { e.stopPropagation(); onUpdateRow(row, { itemType: [] }); setOpen(false); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '7px 14px', border: 'none', cursor: 'pointer',
                  background: 'transparent', color: '#f87171', fontSize: '0.92rem',
                }}
              >
                Clear type
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── One received item row ─────────────────────────────────
function ReceivedRow({ row, skinsData, livePrices, settings, onMarkSold, onUpdateRow }) {
  const img    = findSkinImage(skinsData, row.skin?.value, row.condition?.value);
  const sold   = parseFloat(row.sellPrice) > 0;
  const sell   = parseFloat(row.sellPrice) || 0;
  const profit = parseFloat(row.profit) || 0;
  const mkt    = !sold ? getBuffPrice(livePrices, row) : null;
  const profC  = settings?.profitTextColor || '#4ade80';
  const lossC  = settings?.lossTextColor   || '#f87171';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
      background: 'rgba(255,255,255,0.025)', borderRadius: 10,
      border: `1px solid ${sold ? 'rgba(74,222,128,0.15)' : 'rgba(255,255,255,0.06)'}` }}>
      {/* Image */}
      <div style={{ width: 56, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.18)', borderRadius: 6, padding: '4px 2px' }}>
        {img
          ? <img src={img} alt="" style={{ width: 52, height: 34, objectFit: 'contain' }} />
          : <div style={{ width: 52, height: 34, background: 'rgba(255,255,255,0.04)', borderRadius: 4 }} />
        }
      </div>

      {/* Name + condition */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.97rem', fontWeight: 600, color: '#fff',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.skin?.value || '—'}
        </div>
        {row.condition?.value && (
          <div style={{ fontSize: '0.9rem', color: 'var(--text-dim)' }}>{row.condition.value}</div>
        )}
      </div>

      {/* Type picker */}
      {onUpdateRow && (
        <TypePicker row={row} settings={settings} onUpdateRow={onUpdateRow} />
      )}

      {/* Status / price */}
      <div style={{ flexShrink: 0, textAlign: 'right' }}>
        {sold ? (
          <div>
            <div style={{ fontSize: '0.92rem', color: 'var(--text-muted)' }}>Sold ${sell.toFixed(2)}</div>
            <div style={{ fontSize: '0.98rem', fontWeight: 700, color: profit >= 0 ? profC : lossC }}>
              Net ${sell.toFixed(2)}
            </div>
          </div>
        ) : (
          <div>
            {mkt != null && (
              <div style={{ fontSize: '0.92rem', color: 'var(--text-muted)' }}>
                Buff <strong style={{ color: 'var(--text)' }}>${mkt.toFixed(2)}</strong>
              </div>
            )}
            <div style={{ fontSize: '0.92rem', color: '#f59e0b', fontWeight: 600, marginTop: 2 }}>Holding</div>
          </div>
        )}
      </div>

      {/* Sell button for unsold received items */}
      {!sold && onMarkSold && (
        <button onClick={e => { e.stopPropagation(); onMarkSold(); }} style={{
          flexShrink: 0, padding: '4px 10px', borderRadius: 7, cursor: 'pointer',
          fontFamily: 'var(--font-body)', fontSize: '0.91rem', fontWeight: 700,
          border: '1px solid rgba(245,158,11,0.35)', background: 'rgba(245,158,11,0.08)',
          color: '#f59e0b',
        }}>Sell</button>
      )}
    </div>
  );
}

// ── Trade record card ─────────────────────────────────────
function TradeCard({ offered, received, skinsData, livePrices, csfloatPrices, settings, onMarkSold, onUpdateRow }) {
  const [expanded, setExpanded] = useState(true);

  const img     = findSkinImage(skinsData, offered.skin?.value, offered.condition?.value);
  const buyPrice = parseFloat(offered.buyPrice) || 0;
  const profC   = settings?.profitTextColor || '#4ade80';
  const lossC   = settings?.lossTextColor   || '#f87171';

  // Each received item's own buyPrice already carries its share of what the
  // offered item cost (allocated at trade time, net of any cash received) —
  // so row.profit / (market - buyPrice) already nets that cost, same as any
  // other row. Summing per-row P&L here keeps this in lockstep with Overview/
  // Analytics instead of re-deriving trade P&L a third way.
  const realized = useMemo(() => received.reduce((sum, r) => {
    return parseFloat(r.sellPrice) > 0 ? sum + getRowPnl(r, livePrices) : sum;
  }, 0), [received, livePrices]);

  const unrealized = useMemo(() => received.reduce((sum, r) => {
    return !(parseFloat(r.sellPrice) > 0) ? sum + getRowPnl(r, livePrices) : sum;
  }, 0), [received, livePrices]);

  const cash        = getTradeBoot(offered);
  const pnl         = realized + unrealized;
  const soldCount   = received.filter(r => parseFloat(r.sellPrice) > 0).length;
  const allSold     = received.length > 0 && soldCount === received.length;
  // tradedAt first: `timestamp` is when the row was acquired, which is the
  // trade date only for a row the linker created at trade time.
  const tradedAt    = offered.tradedAt || offered.timestamp;
  const dateStr     = tradedAt ? new Date(tradedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const statusColor  = allSold ? profC : '#f59e0b';
  const statusLabel  = allSold ? 'Complete' : received.length === 0 ? 'No items logged' : `${soldCount}/${received.length} sold`;

  return (
    <div style={{ borderRadius: 16, border: '1px solid var(--border)', background: 'var(--panel)',
      overflow: 'hidden', marginBottom: 12 }}>
      {/* Header row — click to expand/collapse */}
      <div onClick={() => setExpanded(e => !e)} style={{ display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 18px', cursor: 'pointer',
        borderBottom: expanded ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
        {/* Offered item image */}
        <div style={{ width: 72, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.22)', borderRadius: 8, flexShrink: 0, padding: '4px' }}>
          {img
            ? <img src={img} alt="" style={{ width: 64, height: 42, objectFit: 'contain' }} />
            : <div style={{ width: 64, height: 42, background: 'rgba(255,255,255,0.04)', borderRadius: 6 }} />
          }
        </div>

        {/* Offered item info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '1.03rem', fontWeight: 700, color: '#fff',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {offered.skin?.value || '—'}
          </div>
          <div style={{ fontSize: '0.92rem', color: 'var(--text-dim)', marginTop: 2 }}>
            {offered.condition?.value && <span>{offered.condition.value} · </span>}
            Traded {dateStr}
            {buyPrice > 0 && <span style={{ marginLeft: 6 }}>· Cost ${buyPrice.toFixed(2)}</span>}
          </div>
          <TradePartnerLine row={offered} style={{ marginTop: 3 }} />
        </div>

        {/* Type picker for offered item */}
        {onUpdateRow && (
          <div onClick={e => e.stopPropagation()}>
            <TypePicker row={offered} settings={settings} onUpdateRow={onUpdateRow} />
          </div>
        )}

        {/* P&L summary */}
        <div style={{ flexShrink: 0, textAlign: 'right' }}>
          <div style={{ fontSize: '1.09rem', fontWeight: 700,
            color: pnl >= 0 ? profC : lossC }}>
            {received.length === 0
              ? '—'
              : `${pnl >= 0 ? '+' : ''}$${pnl.toFixed(2)}`
            }
          </div>
          <div style={{ fontSize: '0.9rem', fontWeight: 700, color: statusColor, marginTop: 2 }}>
            {statusLabel}
          </div>
        </div>

        {/* Expand arrow */}
        <div style={{ flexShrink: 0, color: 'var(--text-dim)', fontSize: '0.96rem',
          transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Received items */}
          <div>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-dim)',
              textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8, display: 'flex', gap: 10, alignItems: 'center' }}>
              Received ({received.length})
              {cash > 0 && (
                <span style={{ fontSize: '0.89rem', color: '#4ade80', fontWeight: 700,
                  background: 'rgba(74,222,128,0.15)',
                  borderRadius: 999, padding: '2px 8px', textTransform: 'none', letterSpacing: 0 }}>
                  + ${cash.toFixed(2)} cash
                </span>
              )}
            </div>
            {received.length === 0 ? (
              <div style={{ fontSize: '0.95rem', color: 'var(--text-dim)', fontStyle: 'italic' }}>
                No items linked to this trade yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {received.map((r, i) => (
                  <ReceivedRow key={r.assetId || r.timestamp || i} row={r}
                    skinsData={skinsData} livePrices={livePrices} settings={settings}
                    onMarkSold={onMarkSold ? () => onMarkSold(r) : null}
                    onUpdateRow={onUpdateRow}
                  />
                ))}
              </div>
            )}
          </div>

          {/* P&L breakdown */}
          {(received.length > 0 || cash > 0) && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8,
              paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              {[
                { label: 'Cost Basis', value: `$${buyPrice.toFixed(2)}`, color: 'var(--text-muted)' },
                ...(cash > 0 ? [{ label: 'Cash received', value: `$${cash.toFixed(2)}`, color: '#4ade80' }] : []),
                { label: 'Realized', value: soldCount > 0 ? `${realized >= 0 ? '+' : ''}$${realized.toFixed(2)}` : '—', color: realized >= 0 ? 'var(--text)' : lossC },
                { label: 'Unrealized', value: soldCount < received.length ? `${unrealized >= 0 ? '+' : ''}$${unrealized.toFixed(2)}` : '—', color: '#f59e0b' },
                {
                  label: allSold ? 'Final P&L' : 'Running P&L',
                  value: (soldCount > 0 || received.length > 0) ? `${pnl >= 0 ? '+' : ''}$${pnl.toFixed(2)}` : '—',
                  color: pnl >= 0 ? profC : pnl < 0 ? lossC : 'var(--text)',
                  bold: true,
                },
              ].map(stat => (
                <div key={stat.label} style={{ padding: '10px 12px', borderRadius: 10,
                  background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: '0.87rem', color: 'var(--text-dim)', textTransform: 'uppercase',
                    letterSpacing: '0.06em', marginBottom: 4 }}>{stat.label}</div>
                  <div style={{ fontSize: stat.bold ? '1.07rem' : '0.99rem',
                    fontWeight: stat.bold ? 700 : 600, color: stat.color }}>{stat.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main TradedTab ─────────────────────────────────────────
export default function TradedTab({ rows, settings, skinsData, livePrices, csfloatPrices, onMarkReceived, onUpdateRow }) {
  const [search, setSearch] = useState('');

  // Build trade groups: one per unique tradeId
  const tradeGroups = useMemo(() => {
    // Collect all rows with a tradeId (traded-away items)
    const offered = rows.filter(r => r.skin?.value && (r.tradeId || r.tradedFor?.length > 0) && !(parseFloat(r.sellPrice) > 0));

    return offered.map((offeredRow, idx) => {
      const tid = offeredRow.tradeId;
      const received = tid ? rows.filter(r => r.receivedFromTradeId === tid) : [];
      return { offeredRow, received, key: tid || `legacy_${idx}` };
    }).sort((a, b) => new Date(b.offeredRow.timestamp) - new Date(a.offeredRow.timestamp));
  }, [rows]);

  const filtered = useMemo(() => {
    if (!search.trim()) return tradeGroups;
    const q = search.toLowerCase();
    return tradeGroups.filter(({ offeredRow, received }) =>
      offeredRow.skin?.value?.toLowerCase().includes(q) ||
      received.some(r => r.skin?.value?.toLowerCase().includes(q))
    );
  }, [tradeGroups, search]);

  // Summary stats
  const totalBuyPrice = tradeGroups.reduce((s, { offeredRow }) => s + (parseFloat(offeredRow.buyPrice) || 0), 0);
  const completedTrades = tradeGroups.filter(({ received }) => received.length > 0 && received.every(r => parseFloat(r.sellPrice) > 0));
  const profC = settings?.profitTextColor || '#4ade80';

  const inp = {
    padding: '8px 12px', boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 10, color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '0.98rem', outline: 'none',
  };

  if (tradeGroups.length === 0) {
    return (
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '1.09rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>No trades recorded yet</div>
        <div style={{ fontSize: '0.97rem', color: 'var(--text-dim)' }}>
          When you mark an item as Traded, it will appear here with links to the items you received.
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '24px 16px' }}>
      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 20 }}>
        {[
          { label: 'Total Trades', value: tradeGroups.length, color: 'var(--text)' },
          { label: 'Completed', value: completedTrades.length, color: profC },
          { label: 'Pending', value: tradeGroups.length - completedTrades.length, color: '#f59e0b' },
          { label: 'Cost Basis', value: `$${totalBuyPrice.toFixed(2)}`, color: 'var(--text-muted)' },
        ].map(stat => (
          <div key={stat.label} style={{ padding: '12px 16px', borderRadius: 12,
            background: 'var(--panel)', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '0.9rem', color: 'var(--text-dim)', textTransform: 'uppercase',
              letterSpacing: '0.06em', marginBottom: 5 }}>{stat.label}</div>
            <div style={{ fontSize: '1.13rem', fontWeight: 700, color: stat.color }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 14 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search trades by item name…"
          style={{ ...inp, width: '100%' }} />
      </div>

      {/* Trade cards */}
      <div>
        {filtered.map(({ offeredRow, received, key }) => (
          <TradeCard
            key={key}
            offered={offeredRow}
            received={received}
            skinsData={skinsData}
            livePrices={livePrices}
            csfloatPrices={csfloatPrices}
            settings={settings}
            onMarkSold={onMarkReceived
              ? r => {
                  const ri = rows.indexOf(r);
                  if (ri !== -1) onMarkReceived(r, ri);
                }
              : null}
            onUpdateRow={onUpdateRow}
          />
        ))}
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-dim)', fontSize: '0.98rem' }}>
            No trades match
          </div>
        )}
      </div>
    </div>
  );
}
